
import { AIModule } from '../../types';

export const GAMING_AI_MODULE_3: AIModule = {
  id: 'procedural-content-generation',
  title: 'Procedural Content Generation',
  description: 'Learn to create dynamic game content using AI algorithms for levels, quests, narratives, and game assets.',
  duration: '4 hours',
  difficulty: 'hard',
  content: `
# Procedural Content Generation

Procedural Content Generation (PCG) uses algorithms to create game content automatically, enabling infinite replayability, reducing development costs, and creating unique player experiences.

## Types of Procedural Generation

### 1. Level Generation
Creating game environments, maps, and spatial layouts algorithmically.

**Techniques:**
- **Cellular Automata**: Cave and organic structure generation
- **Maze Algorithms**: Dungeon and corridor systems
- **Noise Functions**: Terrain and height maps
- **Grammar-based**: Rule-driven level construction

### 2. Quest Generation
Dynamic creation of missions, objectives, and storylines.

**Components:**
- **Objective Templates**: Reusable quest structures
- **Context Awareness**: Quests that fit current game state
- **Narrative Coherence**: Maintaining story consistency
- **Player Preference**: Adapting to player behavior

### 3. Asset Generation
Creating textures, models, audio, and visual content.

## Advanced PCG Implementation

\`\`\`python
import random
import numpy as np
from typing import List, Dict, Tuple, Optional
from enum import Enum
from dataclasses import dataclass
import math

class TileType(Enum):
    EMPTY = 0
    WALL = 1
    FLOOR = 2
    DOOR = 3
    TREASURE = 4
    ENEMY = 5
    PLAYER_START = 6

class RoomType(Enum):
    CORRIDOR = "corridor"
    CHAMBER = "chamber"
    TREASURY = "treasury"
    BOSS_ROOM = "boss_room"
    ENTRANCE = "entrance"

@dataclass
class Room:
    x: int
    y: int
    width: int
    height: int
    room_type: RoomType
    connected_rooms: List[int]
    entities: List[str]
    
    def get_center(self) -> Tuple[int, int]:
        return (self.x + self.width // 2, self.y + self.height // 2)
    
    def contains_point(self, x: int, y: int) -> bool:
        return self.x <= x < self.x + self.width and self.y <= y < self.y + self.height

class DungeonGenerator:
    """Advanced dungeon generation using multiple algorithms"""
    
    def __init__(self, width: int, height: int, complexity: float = 0.5):
        self.width = width
        self.height = height
        self.complexity = complexity  # 0.0 = simple, 1.0 = complex
        self.grid = np.zeros((height, width), dtype=int)
        self.rooms: List[Room] = []
        self.connections: List[Tuple[int, int]] = []
    
    def generate_dungeon(self) -> np.ndarray:
        """Main dungeon generation pipeline"""
        
        # Step 1: Generate rooms using BSP (Binary Space Partitioning)
        self.generate_rooms_bsp()
        
        # Step 2: Connect rooms with corridors
        self.connect_rooms()
        
        # Step 3: Add details and entities
        self.populate_dungeon()
        
        # Step 4: Apply cellular automata for organic walls
        if self.complexity > 0.7:
            self.apply_cellular_automata()
        
        return self.grid
    
    def generate_rooms_bsp(self):
        """Generate rooms using Binary Space Partitioning"""
        
        class BSPNode:
            def __init__(self, x: int, y: int, width: int, height: int):
                self.x = x
                self.y = y
                self.width = width
                self.height = height
                self.left: Optional[BSPNode] = None
                self.right: Optional[BSPNode] = None
                self.room: Optional[Room] = None
            
            def split(self, min_size: int = 6) -> bool:
                """Split this node into two children"""
                if self.left or self.right:
                    return False  # Already split
                
                # Determine split direction
                split_horizontal = random.choice([True, False])
                if self.width > self.height and self.width / self.height >= 1.25:
                    split_horizontal = False
                elif self.height > self.width and self.height / self.width >= 1.25:
                    split_horizontal = True
                
                max_size = (self.height if split_horizontal else self.width) - min_size
                if max_size <= min_size:
                    return False  # Too small to split
                
                split_pos = random.randint(min_size, max_size)
                
                if split_horizontal:
                    self.left = BSPNode(self.x, self.y, self.width, split_pos)
                    self.right = BSPNode(self.x, self.y + split_pos, self.width, self.height - split_pos)
                else:
                    self.left = BSPNode(self.x, self.y, split_pos, self.height)
                    self.right = BSPNode(self.x + split_pos, self.y, self.width - split_pos, self.height)
                
                return True
            
            def create_room(self, room_id: int) -> Room:
                """Create a room within this node"""
                room_width = random.randint(4, max(4, self.width - 2))
                room_height = random.randint(4, max(4, self.height - 2))
                room_x = self.x + random.randint(1, max(1, self.width - room_width - 1))
                room_y = self.y + random.randint(1, max(1, self.height - room_height - 1))
                
                # Determine room type based on size and position
                room_type = RoomType.CHAMBER
                if room_width * room_height < 20:
                    room_type = RoomType.CORRIDOR
                elif room_width * room_height > 50:
                    room_type = random.choice([RoomType.TREASURY, RoomType.BOSS_ROOM])
                
                self.room = Room(room_x, room_y, room_width, room_height, room_type, [], [])
                return self.room
        
        # Create root node and split recursively
        root = BSPNode(0, 0, self.width, self.height)
        nodes = [root]
        
        # Split nodes based on complexity
        max_splits = int(8 + self.complexity * 12)
        for _ in range(max_splits):
            if not nodes:
                break
            
            node = nodes.pop(random.randint(0, len(nodes) - 1))
            if node.split():
                nodes.extend([node.left, node.right])
        
        # Create rooms in leaf nodes
        def collect_leaf_nodes(node: BSPNode) -> List[BSPNode]:
            if not node.left and not node.right:
                return [node]
            
            leaves = []
            if node.left:
                leaves.extend(collect_leaf_nodes(node.left))
            if node.right:
                leaves.extend(collect_leaf_nodes(node.right))
            return leaves
        
        leaf_nodes = collect_leaf_nodes(root)
        for i, node in enumerate(leaf_nodes):
            room = node.create_room(i)
            self.rooms.append(room)
            self.place_room(room)
    
    def place_room(self, room: Room):
        """Place room tiles in the grid"""
        for y in range(room.y, room.y + room.height):
            for x in range(room.x, room.x + room.width):
                if 0 <= x < self.width and 0 <= y < self.height:
                    if x == room.x or x == room.x + room.width - 1 or y == room.y or y == room.y + room.height - 1:
                        self.grid[y][x] = TileType.WALL.value
                    else:
                        self.grid[y][x] = TileType.FLOOR.value
    
    def connect_rooms(self):
        """Connect rooms with corridors using minimum spanning tree"""
        if len(self.rooms) < 2:
            return
        
        # Calculate distances between all room centers
        room_centers = [room.get_center() for room in self.rooms]
        edges = []
        
        for i in range(len(self.rooms)):
            for j in range(i + 1, len(self.rooms)):
                x1, y1 = room_centers[i]
                x2, y2 = room_centers[j]
                distance = math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2)
                edges.append((distance, i, j))
        
        # Sort edges by distance
        edges.sort()
        
        # Use Union-Find for minimum spanning tree
        parent = list(range(len(self.rooms)))
        
        def find(x):
            if parent[x] != x:
                parent[x] = find(parent[x])
            return parent[x]
        
        def union(x, y):
            px, py = find(x), find(y)
            if px != py:
                parent[px] = py
                return True
            return False
        
        # Create minimum spanning tree
        for distance, room1, room2 in edges:
            if union(room1, room2):
                self.connections.append((room1, room2))
                self.create_corridor(self.rooms[room1], self.rooms[room2])
                self.rooms[room1].connected_rooms.append(room2)
                self.rooms[room2].connected_rooms.append(room1)
        
        # Add some extra connections for complexity
        extra_connections = int(self.complexity * len(self.rooms) * 0.3)
        for _ in range(extra_connections):
            room1, room2 = random.sample(range(len(self.rooms)), 2)
            if room2 not in self.rooms[room1].connected_rooms:
                self.create_corridor(self.rooms[room1], self.rooms[room2])
                self.rooms[room1].connected_rooms.append(room2)
                self.rooms[room2].connected_rooms.append(room1)
    
    def create_corridor(self, room1: Room, room2: Room):
        """Create corridor between two rooms"""
        x1, y1 = room1.get_center()
        x2, y2 = room2.get_center()
        
        # Create L-shaped corridor
        if random.choice([True, False]):
            # Horizontal first, then vertical
            self.carve_horizontal_corridor(x1, x2, y1)
            self.carve_vertical_corridor(x2, y1, y2)
        else:
            # Vertical first, then horizontal
            self.carve_vertical_corridor(x1, y1, y2)
            self.carve_horizontal_corridor(x1, x2, y2)
    
    def carve_horizontal_corridor(self, x1: int, x2: int, y: int):
        """Carve horizontal corridor"""
        for x in range(min(x1, x2), max(x1, x2) + 1):
            if 0 <= x < self.width and 0 <= y < self.height:
                self.grid[y][x] = TileType.FLOOR.value
    
    def carve_vertical_corridor(self, x: int, y1: int, y2: int):
        """Carve vertical corridor"""
        for y in range(min(y1, y2), max(y1, y2) + 1):
            if 0 <= x < self.width and 0 <= y < self.height:
                self.grid[y][x] = TileType.FLOOR.value
    
    def populate_dungeon(self):
        """Add entities, treasures, and other content"""
        
        # Place player start in first room
        if self.rooms:
            start_room = self.rooms[0]
            start_room.room_type = RoomType.ENTRANCE
            center_x, center_y = start_room.get_center()
            self.grid[center_y][center_x] = TileType.PLAYER_START.value
        
        # Place content based on room types
        for room in self.rooms:
            if room.room_type == RoomType.TREASURY:
                # Place treasure
                center_x, center_y = room.get_center()
                self.grid[center_y][center_x] = TileType.TREASURE.value
                room.entities.append("treasure_chest")
            
            elif room.room_type == RoomType.BOSS_ROOM:
                # Place boss enemy
                center_x, center_y = room.get_center()
                self.grid[center_y][center_x] = TileType.ENEMY.value
                room.entities.append("boss_enemy")
            
            elif room.room_type == RoomType.CHAMBER:
                # Random chance for enemies or items
                if random.random() < 0.6:
                    # Place regular enemies
                    enemy_count = random.randint(1, 3)
                    for _ in range(enemy_count):
                        # Find random floor tile in room
                        attempts = 10
                        while attempts > 0:
                            x = random.randint(room.x + 1, room.x + room.width - 2)
                            y = random.randint(room.y + 1, room.y + room.height - 2)
                            if self.grid[y][x] == TileType.FLOOR.value:
                                self.grid[y][x] = TileType.ENEMY.value
                                room.entities.append("enemy")
                                break
                            attempts -= 1
    
    def apply_cellular_automata(self, iterations: int = 3):
        """Apply cellular automata for organic wall generation"""
        for _ in range(iterations):
            new_grid = self.grid.copy()
            
            for y in range(1, self.height - 1):
                for x in range(1, self.width - 1):
                    if self.grid[y][x] == TileType.WALL.value:
                        # Count neighboring walls
                        wall_count = 0
                        for dy in [-1, 0, 1]:
                            for dx in [-1, 0, 1]:
                                if self.grid[y + dy][x + dx] == TileType.WALL.value:
                                    wall_count += 1
                        
                        # Apply rules
                        if wall_count < 4:
                            new_grid[y][x] = TileType.EMPTY.value
                        elif wall_count > 6:
                            new_grid[y][x] = TileType.WALL.value
            
            self.grid = new_grid
    
    def print_dungeon(self):
        """Print ASCII representation of dungeon"""
        symbols = {
            TileType.EMPTY.value: ' ',
            TileType.WALL.value: '#',
            TileType.FLOOR.value: '.',
            TileType.DOOR.value: '+',
            TileType.TREASURE.value: '$',
            TileType.ENEMY.value: 'E',
            TileType.PLAYER_START.value: '@'
        }
        
        for row in self.grid:
            print(''.join(symbols.get(cell, '?') for cell in row))

class QuestGenerator:
    """Dynamic quest generation system"""
    
    def __init__(self):
        self.quest_templates = self.setup_quest_templates()
        self.story_context = {
            'main_threat': 'ancient_evil',
            'player_reputation': 0.5,
            'faction_standing': {},
            'completed_quests': []
        }
    
    def setup_quest_templates(self) -> List[Dict]:
        """Define quest templates with parameters"""
        return [
            {
                'id': 'fetch_quest',
                'title': 'Retrieve the {item}',
                'description': 'A {npc_type} needs you to find the {item} located in the {location}.',
                'objectives': [
                    'Travel to {location}',
                    'Find the {item}',
                    'Return to {quest_giver}'
                ],
                'parameters': {
                    'item': ['Ancient Artifact', 'Lost Scroll', 'Magic Crystal', 'Sacred Relic'],
                    'npc_type': ['wizard', 'merchant', 'noble', 'priest'],
                    'location': ['Abandoned Temple', 'Dark Cave', 'Ruined Tower', 'Forgotten Crypt'],
                    'quest_giver': ['Wise Scholar', 'Town Elder', 'Royal Advisor', 'High Priest']
                },
                'rewards': ['gold', 'experience', 'item', 'reputation'],
                'difficulty': 'medium'
            },
            {
                'id': 'elimination_quest',
                'title': 'Eliminate the {threat}',
                'description': 'The {threat} is terrorizing {location}. Defeat it to restore peace.',
                'objectives': [
                    'Locate the {threat} in {location}',
                    'Defeat the {threat}',
                    'Report victory to {quest_giver}'
                ],
                'parameters': {
                    'threat': ['Bandit Leader', 'Corrupted Beast', 'Rogue Mage', 'Undead Champion'],
                    'location': ['Nearby Forest', 'Mountain Pass', 'Old Ruins', 'Swampland'],
                    'quest_giver': ['Town Guard Captain', 'Village Chief', 'Local Hero', 'Concerned Citizen']
                },
                'rewards': ['gold', 'experience', 'weapon'],
                'difficulty': 'hard'
            },
            {
                'id': 'escort_quest',
                'title': 'Escort {character} to {destination}',
                'description': '{character} needs safe passage to {destination} through dangerous territory.',
                'objectives': [
                    'Meet {character} at the starting location',
                    'Safely escort {character} to {destination}',
                    'Ensure {character} arrives unharmed'
                ],
                'parameters': {
                    'character': ['Traveling Merchant', 'Royal Messenger', 'Injured Soldier', 'Lost Child'],
                    'destination': ['Capital City', 'Remote Village', 'Military Outpost', 'Holy Shrine'],
                },
                'rewards': ['gold', 'reputation', 'information'],
                'difficulty': 'medium'
            }
        ]
    
    def generate_quest(self, player_level: int, location_context: str) -> Dict:
        """Generate a quest based on player level and context"""
        
        # Select appropriate template based on difficulty
        suitable_templates = []
        for template in self.quest_templates:
            if template['difficulty'] == 'easy' and player_level <= 5:
                suitable_templates.append(template)
            elif template['difficulty'] == 'medium' and 3 <= player_level <= 15:
                suitable_templates.append(template)
            elif template['difficulty'] == 'hard' and player_level >= 10:
                suitable_templates.append(template)
        
        if not suitable_templates:
            suitable_templates = self.quest_templates
        
        template = random.choice(suitable_templates)
        
        # Fill in template parameters
        quest = {
            'id': f"{template['id']}_{random.randint(1000, 9999)}",
            'template_id': template['id'],
            'title': template['title'],
            'description': template['description'],
            'objectives': template['objectives'].copy(),
            'rewards': self.generate_rewards(template['rewards'], player_level),
            'difficulty': template['difficulty'],
            'parameters': {}
        }
        
        # Fill in parameters
        for param, options in template['parameters'].items():
            quest['parameters'][param] = random.choice(options)
        
        # Replace placeholders in title, description, and objectives
        for key in ['title', 'description']:
            for param, value in quest['parameters'].items():
                quest[key] = quest[key].replace(f'{{{param}}}', value)
        
        for i, objective in enumerate(quest['objectives']):
            for param, value in quest['parameters'].items():
                quest['objectives'][i] = objective.replace(f'{{{param}}}', value)
        
        return quest
    
    def generate_rewards(self, reward_types: List[str], player_level: int) -> Dict:
        """Generate appropriate rewards based on player level"""
        rewards = {}
        
        for reward_type in reward_types:
            if reward_type == 'gold':
                rewards['gold'] = random.randint(50 * player_level, 150 * player_level)
            elif reward_type == 'experience':
                rewards['experience'] = random.randint(100 * player_level, 300 * player_level)
            elif reward_type == 'item':
                item_tiers = ['common', 'uncommon', 'rare']
                tier_weights = [0.6, 0.3, 0.1] if player_level < 10 else [0.3, 0.5, 0.2]
                rewards['item'] = {
                    'tier': random.choices(item_tiers, weights=tier_weights)[0],
                    'type': random.choice(['weapon', 'armor', 'accessory', 'consumable'])
                }
            elif reward_type == 'reputation':
                rewards['reputation'] = random.randint(5, 15)
        
        return rewards

# Example usage
def demonstrate_pcg():
    """Demonstrate procedural content generation"""
    
    print("=== Procedural Dungeon Generation ===")
    
    # Generate dungeon
    generator = DungeonGenerator(40, 20, complexity=0.7)
    dungeon = generator.generate_dungeon()
    
    print(f"Generated dungeon with {len(generator.rooms)} rooms")
    generator.print_dungeon()
    
    print("\\n=== Room Details ===")
    for i, room in enumerate(generator.rooms):
        print(f"Room {i}: {room.room_type.value} at ({room.x}, {room.y}) size {room.width}x{room.height}")
        if room.entities:
            print(f"  Entities: {', '.join(room.entities)}")
    
    print("\\n=== Dynamic Quest Generation ===")
    
    # Generate quests
    quest_gen = QuestGenerator()
    
    for player_level in [1, 5, 10, 15]:
        quest = quest_gen.generate_quest(player_level, "forest_region")
        print(f"\\nQuest for Level {player_level} Player:")
        print(f"Title: {quest['title']}")
        print(f"Description: {quest['description']}")
        print("Objectives:")
        for obj in quest['objectives']:
            print(f"  - {obj}")
        print(f"Rewards: {quest['rewards']}")

# Run demonstration
demonstrate_pcg()
\`\`\`

## Advanced PCG Techniques

### Genetic Algorithms for Level Design
Evolution-based optimization of level layouts for specific gameplay metrics.

### Machine Learning for Content Generation
Using neural networks to learn from existing content and generate new variations.

### Constraint-Based Generation
Ensuring generated content meets specific design requirements and constraints.

### Player-Adaptive Generation
Modifying generated content based on player behavior and preferences.

Procedural Content Generation enables games to provide unique experiences for every player while maintaining quality and coherence in the generated content.
  `,
  quiz: {
    id: 'pcg-quiz',
    title: 'Procedural Content Generation Quiz',
    passThreshold: 75,
    questions: [
      {
        id: 'q1',
        question: 'What is the main advantage of using Binary Space Partitioning (BSP) for dungeon generation?',
        type: 'multiple-choice',
        options: [
          'It creates perfectly square rooms',
          'It guarantees all rooms are connected',
          'It creates hierarchical room layouts with controlled subdivision',
          'It is the fastest generation algorithm'
        ],
        correctAnswer: 2,
        explanation: 'BSP recursively subdivides space to create hierarchical room layouts with controlled proportions and logical spatial relationships.',
        difficulty: 'hard',
        points: 20
      },
      {
        id: 'q2',
        question: 'In quest generation, what is a template parameter?',
        type: 'multiple-choice',
        options: [
          'A fixed quest objective',
          'A placeholder that gets filled with random content',
          'The difficulty level of the quest',
          'The reward amount'
        ],
        correctAnswer: 1,
        explanation: 'Template parameters are placeholders in quest templates that get replaced with randomly selected appropriate content to create quest variations.',
        difficulty: 'medium',
        points: 15
      }
    ]
  }
};
